import type { SelectOption } from '@/components/ui/Select';
import { SELF_ID_ANSWERS, SELF_ID_ANSWER_LABEL } from '@/lib/constants/self-identification';

/**
 * Create-startup option lists. Values mirror the backend Prisma enums exactly
 * (the wire value is the enum member name, not the DB-mapped string); labels
 * are display-only. Keep in sync with `CreateStartupDto` enums.
 */
// Now also used by the Investor Profile module (Preferred Funding Stages) -
// promoted to `@/lib/constants/funding-stages.ts`, re-exported here so
// existing imports in this feature keep working unchanged.
export { FUNDING_STAGE_OPTIONS } from '@/lib/constants/funding-stages';

/** Plain Yes/No choice (Past Exits, "Do you have a lead investor?"). */
export const YES_NO_OPTIONS: SelectOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

// data-fields.md is Yes/No (client decision 2026-07-22), not Figma's free text.
export const PAST_EXITS_OPTIONS: SelectOption[] = YES_NO_OPTIONS;

// Shared with the admin console types - lives in `@/lib/constants`, re-exported
// here so this feature's imports stay local.
export {
  SELF_ID_ANSWERS,
  SELF_ID_ANSWER_LABEL,
  type SelfIdAnswer,
} from '@/lib/constants/self-identification';

export const SELF_ID_ANSWER_OPTIONS: SelectOption[] = SELF_ID_ANSWERS.map((answer) => ({
  value: answer,
  label: SELF_ID_ANSWER_LABEL[answer],
}));

/**
 * The catch-all sector's exact name in the taxonomy (`GET /taxonomy/sectors`).
 * It has no subsectors; choosing it asks for a free-text `sectorOther` instead
 * (UX sheet r104).
 */
export const SECTOR_OTHER_NAME = 'Other';
export const MAX_SECTOR_OTHER_LENGTH = 100;

export const INSTRUMENT_OPTIONS: SelectOption[] = [
  { value: 'safe_convertible_note', label: 'SAFE / Convertible note' },
  { value: 'priced_round', label: 'Priced round' },
];

/** Labels for the Step-3 segmented control (Figma 197:685). */
export const INSTRUMENT_SEGMENT_OPTIONS: SelectOption[] = [
  { value: 'safe_convertible_note', label: 'SAFE / Convertible Note' },
  { value: 'priced_round', label: 'Priced Round' },
];

export const MARKET_MODEL_OPTIONS: SelectOption[] = [
  { value: 'b2b', label: 'B2B' },
  { value: 'b2c', label: 'B2C' },
  { value: 'b2g', label: 'B2G' },
  { value: 'saas', label: 'SaaS' },
  { value: 'ai', label: 'AI' },
];

export const AI_CLASSIFICATION_OPTIONS: SelectOption[] = [
  { value: 'core_ai_product', label: 'Core AI product' },
  { value: 'ai_enabled', label: 'AI-enabled' },
  { value: 'ai_infrastructure_tooling', label: 'AI infrastructure / tooling' },
  { value: 'agentic_ai', label: 'Agentic AI' },
  { value: 'not_an_ai_company', label: 'Not an AI company' },
];

// Bucket labels exactly as the client CSV (2026-07-16); investor Minimum
// Revenue Requirement uses the same buckets.
export const REVENUE_RANGE_OPTIONS: SelectOption[] = [
  { value: 'zero', label: '$0' },
  { value: 'under_250k', label: 'Under $250K' },
  { value: 'range_250k_500k', label: '$250K-$500K' },
  { value: 'range_500k_1m', label: '$500K-$1M' },
  { value: 'range_1m_2m', label: '$1M-$2M' },
  { value: 'range_2m_5m', label: '$2M-$5M' },
  { value: 'range_5m_10m', label: '$5M-$10M' },
  { value: 'range_10m_plus', label: '$10M+' },
];

// Client confirmed 2026-07-16: the extended 8 buckets are final ("the old
// $2M+ ceiling is too low"); investor Minimum Revenue uses the same buckets.

export const REVENUE_MODEL_OPTIONS: SelectOption[] = [
  { value: 'recurring_subscription', label: 'Recurring or subscription' },
  { value: 'transactional_usage', label: 'Transactional or usage-based' },
  { value: 'one_time_project', label: 'One-time or project' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'pre_revenue', label: 'Pre-revenue, not applicable' },
];

export const REVENUE_GROWTH_RATE_OPTIONS: SelectOption[] = [
  { value: 'pre_revenue', label: 'Pre-revenue' },
  { value: 'flat_declining', label: 'Flat or declining' },
  { value: 'under_50pct', label: 'Under 50% per year' },
  { value: 'growth_50_100pct', label: '50-100% per year' },
  { value: 'growth_100pct_plus', label: '100%+ per year' },
];

export const PAYING_CUSTOMERS_OPTIONS: SelectOption[] = [
  { value: 'none', label: 'None' },
  { value: 'one', label: '1' },
  { value: 'two', label: '2' },
  { value: 'three', label: '3' },
  { value: 'four', label: '4' },
  { value: 'five_to_ten', label: '5 - 10' },
  { value: 'eleven_to_fifty', label: '11 - 50' },
  { value: 'fifty_one_to_250', label: '51 - 250' },
  { value: 'customers_250_plus', label: '250+' },
];

// Now also used by the Investor Profile module (Underrepresented Founders
// Part 2) - promoted to `@/lib/constants/underrepresented-categories.ts`,
// re-exported here so existing imports in this feature keep working.
export { UNDERREPRESENTED_CATEGORY_OPTIONS } from '@/lib/constants/underrepresented-categories';

export const INVESTOR_TYPE_OPTIONS: SelectOption[] = [
  { value: 'angel', label: 'Angel' },
  { value: 'vc_fund', label: 'VC fund' },
  { value: 'family_office', label: 'Family office' },
  { value: 'corporate_strategic', label: 'Corporate / strategic' },
  { value: 'open_to_all', label: 'Open to all' },
];

// Sector/Subsector are no longer a hardcoded list here - the taxonomy (VF
// Sector Taxonomy CSV, "shared Sector/Subsector enum - single source MNR <->
// Capital Raise") is served from the Backend (`GET /taxonomy/sectors`, seeded
// from the same CSV) via `useSectors()` (`features/startups/hooks/`), so the
// client can add a sector/subsector later with a data change instead of a
// frontend deploy. See `Backend/src/prisma/seed-taxonomy.ts`.

export const MAX_FOUNDING_TEAM = 5;
export const MAX_SUBSECTORS = 2;
export const MAX_DESCRIPTION_LENGTH = 500;

export const CREATE_STARTUP_STEPS = [
  { key: 'company', label: 'Company Info' },
  { key: 'team', label: 'Founding Team' },
  { key: 'raise', label: 'Current Raise' },
  { key: 'review', label: 'Review & Save' },
] as const;

export const FIELD_LABELS = {
  companyName: 'Company Name',
  companyWebsiteUrl: 'Company Website',
  companyLinkedinUrl: 'Company LinkedIn',
  briefDescription: 'Brief Description',
  sector: 'Select Sector',
  fundingStage: 'Funding Stage',
  yearFounded: 'Year Founded',
  hqCountry: 'HQ Country',
  hqState: 'HQ State',
  hqCity: 'HQ City',
  marketModels: 'Market Model',
  memberName: 'Name',
  memberTitle: 'Title',
  memberLinkedin: 'LinkedIn',
  memberPastExits: 'Past Exits',
  financeSoughtLow: 'Minimum Round Size',
  financeSoughtHigh: 'Maximum Round Size',
  raiseRange: 'Raise Range',
  valuationOrCap: 'Valuation or Cap',
  instrument: 'Instrument',
  targetCloseDate: 'Target Close Date',
  leadInvestorName: 'Lead Investor',
  hasLeadInvestor: 'Do you have a lead investor?',
  sectorOther: 'Your sector',
  workEmail: 'Work email',
  logo: 'Upload Company Logo',
  pitchDeck: 'Upload Pitch Deck',
  pitchVideo: 'Pitch Video Link',
  subsectors: 'Subsectors',
  totalRevenueRange: 'Total Revenue',
  arr: 'ARR',
  amountRaisedToDate: 'Total Amount Raised to Date',
  // Client CSV 2026-07-16: "Note that it's nondilutive, not dilutive" -
  // Figma's "Dilutive Funding" label was wrong.
  nondilutiveFunding: 'Total Non-Dilutive Funding Raised To Date',
  recentMilestone: 'Recent Milestone',
  aiClassification: 'AI Classification',
  revenueModel: 'Revenue Model',
  revenueGrowthRate: 'Revenue Growth Rate',
  payingCustomers: 'Paying Customers',
  investorTypesSought: 'Investor Types Sought',
  founderFemale: 'Female-led founding team',
  founderUnderrepresented: 'Underrepresented founders',
  founderUnderrepresentedCategories: 'Self-identification',
  // Form questions (UX sheet r113/r114); the short labels above are for the
  // read-only views.
  founderFemaleQuestion:
    'Please check if at least one of your C-Suite founders identifies as a woman.',
  founderUnderrepresentedQuestion:
    'Please check if at least one of your C-Suite founders identifies as a member of an underrepresented community.',
  employeeCount: 'Employee Count',
  useOfFunds: 'Use of Funds',
} as const;

export const SECTION_TITLES = {
  BASIC_DETAILS: 'Company Details',
  SECTOR_STAGE: 'Sector & Stage',
  FINANCIALS: 'Financials & Fundraising',
  UPLOADS_LINKS: 'Uploads and Links',
  FOUNDING_TEAM: 'Founding Team',
  CURRENT_RAISE: 'Current Raise',
  REVIEW: 'Review Your Startup Profile',
  SAVE_CARD: 'Ready to Save Your Profile?',
} as const;

export const FIELD_PLACEHOLDERS = {
  briefDescription:
    'Tell us about your company - what you do, who you serve, and why it matters',
  recentMilestone:
    'Share a recent win - a key partnership, product launch, revenue milestone, or customer achievement',
  memberName: 'Full name',
  memberTitle: 'e.g. CEO, CTO, COO',
  memberLinkedin: 'LinkedIn profile URL',
  leadInvestorName: 'Lead investor name if confirmed',
  targetCloseDate: 'Select target close date',
  money: '0',
  companyName: 'Acme Robotics, Inc.',
  companyWebsiteUrl: 'https://acmerobotics.com',
  companyLinkedinUrl: 'https://linkedin.com/company/acmerobotics',
  employeeCount: '12',
  yearFounded: '2023',
  useOfFunds: 'e.g. 60% engineering hires, 30% go-to-market, 10% runway',
} as const;

export const FIELD_HELPERS = {
  raiseRange: 'Enter the floor and ceiling of your fundraising range',
  valuationOrCap: 'Enter valuation for priced rounds or cap for SAFEs',
  logo: 'PNG or JPG, square recommended, max 5MB',
  pitchDeck: 'PDF only, max 50MB. Recipients with access will be able to view this.',
} as const;

/** Cross-field error when the round-size range is inverted (wizard and edit page). */
export const RAISE_RANGE_ERROR = 'Maximum Round Size must be at least Minimum Round Size';

export const SECTOR_OTHER_LENGTH_ERROR = `Keep it under ${MAX_SECTOR_OTHER_LENGTH} characters`;

export const SELECT_PLACEHOLDER = 'Select an option';

export const CREATE_STARTUP_STRINGS = {
  PAGE_TITLE: 'Create Startup Profile',
  NEXT_PREFIX: 'Next: ',
  BACK: 'Back',
  ADD_MEMBER: 'Add Co-Founder',
  MEMBER_TITLE_PREFIX: 'Co-Founder ',
  TEAM_SUBTITLE: 'Add details for each co-founder. You can add up to 5 team members.',
  RAISE_SUBTITLE: 'Tell investors about your current fundraising round.',
  REMOVE: 'Remove',
  EDIT: 'Edit',
  DOMAIN_ALREADY_REGISTERED_ERROR:
    'A startup profile for this company domain already exists. Please ask an existing admin to invite you instead of creating a new one.',
  // Shown as a full-page gate before the wizard - see
  // useStartupEligibility/StartupAlreadyExistsState. Distinct from the
  // Step-4 DOMAIN_ALREADY_REGISTERED_ERROR above, which stays a manual
  // "ask an admin" message (no request-access affordance in that inline
  // form-error context).
  STARTUP_EXISTS_TITLE: 'Startup Profile Already Exists',
  // Fallback only, used as a plain sentence when the eligibility check's
  // error carries no company name (e.g. a network-layer failure with no
  // structured body). When it does, STARTUP_EXISTS_DESCRIPTION_PREFIX/
  // SUFFIX wrap the Backend-provided bare name instead (highlighted inline
  // by StartupAlreadyExistsState), same pattern as the shared-profile OTP
  // screen's OTP_SUBTITLE_PREFIX/SUFFIX around a bolded email.
  STARTUP_EXISTS_DESCRIPTION_FALLBACK:
    "We found an existing startup profile for your company's email domain. Request access below and we'll notify their admin to add you as a team member.",
  STARTUP_EXISTS_DESCRIPTION_PREFIX: 'An existing startup profile was found for ',
  STARTUP_EXISTS_DESCRIPTION_SUFFIX:
    ". Request access below and we'll notify their admin to add you as a team member.",
  STARTUP_EXISTS_REQUEST_ACTION: 'Request Access',
  STARTUP_EXISTS_REQUEST_SENDING: 'Sending Request…',
  STARTUP_EXISTS_REQUEST_SENT: 'Request Sent',
  STARTUP_EXISTS_REQUEST_SUCCESS: "Request sent - we've notified their admin to add you as a team member.",
  STARTUP_EXISTS_REQUEST_ERROR: 'We could not send your request. Please try again.',
  STARTUP_EXISTS_REQUEST_COOLDOWN: "You've already requested access recently - no need to send another one yet.",
  STARTUP_EXISTS_ACTION: 'Go to Dashboard',
  // Shown as the same full-page gate slot when the account's own email is
  // personal/social (FR-14, MYN-76) - checked before STARTUP_EXISTS_TITLE
  // above, since `checkEligibility` rejects on this first.
  WORK_EMAIL_REQUIRED_TITLE: 'Update Your Personal Email',
  WORK_EMAIL_REQUIRED_DESCRIPTION:
    'To create a startup profile, update your personal email to a company email first.',
  WORK_EMAIL_REQUIRED_ACTION: 'Update Email',
  WORK_EMAIL_REQUIRED_DASHBOARD_ACTION: 'Go to Dashboard',
  SAVE: 'Save Startup Profile',
  SAVING: 'Saving…',
  SAVE_ERROR: 'We could not create your startup. Please try again.',
  REVIEW_INTRO: 'Review your details before saving. You can go back to edit any section.',
  SAVE_CARD_INTRO:
    'Your profile will be saved and visible to authorized team members and MNR Admins. You can update it anytime from your dashboard.',
} as const;

/**
 * "Confirm your startup", the Issue Report journey's only startup step
 * (FR-225, MYN-300). The rest of the profile is completed after payment.
 */
export const CONFIRM_STARTUP_STRINGS = {
  TITLE: 'Confirm your startup',
  SUBTITLE:
    "The Issue Report is bought for your startup. We've filled in what you gave us at registration. Check it, or change it here.",
  NAME_LABEL: 'Company name',
  NAME_REQUIRED: 'Company name is required',
  WEBSITE_LABEL: 'Company website',
  WEBSITE_REQUIRED: 'Company website is required',
  EMAIL_LABEL: 'Company email',
  ROLE_LABEL: 'Your role',
  ROLE_VALUE: 'Startup Admin',
  LATER_NOTE: 'You can add your team, current raise and pitch deck after payment, from your dashboard.',
  SUBMIT: 'Create startup and continue to payment',
  SUBMITTING: 'Creating your startup...',
  FAILED: 'We could not create your startup. Please try again.',
  // `{company}` is the existing startup's name; the fallback has none.
  EXISTS_NOTE:
    "Only a startup's admin can buy the Issue Report. Your request goes to the admin of {company}, who can buy it once you're on the team, or make you an admin.",
  EXISTS_NOTE_FALLBACK:
    "Only a startup's admin can buy the Issue Report. Your request goes to that startup's admin, who can buy it once you're on the team, or make you an admin.",
} as const;

